import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // ⚠️ PROTECTED FUNCTION - DO NOT MODIFY OR ADD ASYNC OPERATIONS
  // This is a Supabase auth state change listener that must remain synchronous
  const handleAuthStateChange = (event, session) => {
    console.log('Auth state change:', event, session?.user?.id);
    
    // SYNC OPERATIONS ONLY - NO ASYNC/AWAIT ALLOWED
    if (session?.user) {
      setUser(session?.user);
      // Fetch user profile after user is set
      fetchUserProfile(session?.user?.id);
    } else {
      setUser(null);
      setUserProfile(null);
    }
    
    if (!initialized) {
      setInitialized(true);
    }
    
    setLoading(false);
  };

  const fetchUserProfile = async (userId) => {
    try {
      // Add additional error handling and retry logic
      if (!userId) {
        console.warn('fetchUserProfile called without userId');
        setUserProfile(null);
        return;
      }

      const { data: profile, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        console.error('Error fetching user profile:', error);
        
        // If profile doesn't exist, this is expected for new users
        if (error?.code === 'PGRST116') {
          console.log('User profile not found - this is normal for new accounts');
          setUserProfile(null);
        } else {
          // For other errors, log but don't crash console.error('Unexpected error fetching profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(profile);
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        // Get initial session with enhanced error handling
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          
          // Handle refresh token errors specifically
          if (error?.message?.includes('refresh_token_not_found') || 
              error?.message?.includes('Invalid Refresh Token')) {
            console.log('Refresh token invalid or not found - clearing session and requiring re-login');
            await supabase?.auth?.signOut();
            if (mounted) {
              setUser(null);
              setUserProfile(null);
              setLoading(false);
              setInitialized(true);
            }
            return;
          }
          
          // Don't clear session for network errors - retry instead
          if (error?.message?.includes('Load failed') || error?.message?.includes('network')) {
            console.log('Network error during session fetch, retrying in 2 seconds...');
            setTimeout(() => {
              if (mounted) {
                initializeAuth();
              }
            }, 2000);
            return;
          }
          
          // Clear corrupted session data for auth errors
          await supabase?.auth?.signOut();
          if (mounted) {
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            setInitialized(true);
          }
          return;
        }
        
        if (mounted) {
          if (session?.user) {
            setUser(session?.user);
            fetchUserProfile(session?.user?.id);
          } else {
            setUser(null);
            setUserProfile(null);
          }
          setLoading(false);
          setInitialized(true);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        
        // Enhanced error handling for "Load failed" errors
        if (error?.message?.includes('Load failed')) {
          console.log('Network/Load error during auth initialization, retrying...');
          if (mounted) {
            setTimeout(() => {
              initializeAuth();
            }, 3000);
          }
          return;
        }
        
        if (mounted) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(handleAuthStateChange);

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Enhanced session management with error handling
  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase?.auth?.refreshSession();
      
      if (error) {
        console.error('Error refreshing session:', error);
        
        // Handle refresh token errors - require re-login
        if (error?.message?.includes('refresh_token_not_found') || 
            error?.message?.includes('Invalid Refresh Token')) {
          console.log('Refresh token invalid - clearing session');
          await supabase?.auth?.signOut();
          setUser(null);
          setUserProfile(null);
          return { data: null, error: { message: 'Session expired. Please log in again.' } };
        }
        
        // If refresh fails, clear session
        await supabase?.auth?.signOut();
        return { data: null, error };
      }
      
      return { data: session, error: null };
    } catch (error) {
      console.error('Error in refreshSession:', error);
      return { data: null, error };
    }
  };

  // Authentication methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signUp = async (email, password, username, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            username,
            full_name: fullName,
            role: 'user'
          }
        }
      });
      
      if (error) throw error;

      // Enhanced profile creation with multiple fallback strategies
      if (data?.user) {
        // Wait a moment for auth user to be fully created
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Strategy 1: Try the safe creation function first
        try {
          console.log('Attempting profile creation via safe function...');
          const { data: profileData, error: profileError } = await supabase?.rpc('create_user_profile_safe', {
            profile_id: data?.user?.id,
            profile_email: email,
            profile_username: username,
            profile_full_name: fullName || username,
            profile_role: 'user',
            profile_is_active: true
          });

          if (!profileError) {
            console.log('Profile created successfully via safe function');
            return { data, error: null };
          } else {
            console.warn('Safe profile creation had issues:', profileError);
            // Continue to fallback strategies
          }
        } catch (safeError) {
          console.warn('Safe profile creation failed, trying simple function:', safeError);
        }

        // Strategy 2: Try the simple function as backup
        try {
          console.log('Attempting profile creation via simple function...');
          const { data: simpleProfileData, error: simpleError } = await supabase?.rpc('create_user_profile_simple', {
            profile_id: data?.user?.id,
            profile_email: email,
            profile_username: username,
            profile_full_name: fullName || username
          });

          if (!simpleError) {
            console.log('Profile created successfully via simple function');
            return { data, error: null };
          } else {
            console.warn('Simple profile creation had issues:', simpleError);
          }
        } catch (simpleError) {
          console.warn('Simple profile creation failed, trying direct insertion:', simpleError);
        }

        // Strategy 3: Direct insertion as final fallback
        try {
          console.log('Attempting direct profile insertion...');
          const { data: directData, error: directError } = await supabase?.from('user_profiles')?.insert({
              id: data?.user?.id,
              email: email,
              username: username,
              full_name: fullName || username,
              role: 'user',
              is_active: true,
              email_verified: false
            })?.select()?.single();

          if (!directError) {
            console.log('Profile created successfully via direct insertion');
            return { data, error: null };
          } else {
            console.warn('Direct profile creation failed:', directError);
          }
        } catch (directError) {
          console.error('All profile creation strategies failed:', directError);
        }

        // Even if profile creation fails, the auth user was created successfully
        console.log('Auth user created but profile creation had issues - user can still sign in');
        return { 
          data, 
          error: null, 
          warning: 'Account created but profile setup incomplete. Please contact support if you experience issues.' 
        };
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('SignUp error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      // First, check if there's an active session before attempting sign out
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      // If there's no session or error getting session, just clear local state
      if (sessionError || !session) {
        console.log('No active session found, clearing local auth state');
        setUser(null);
        setUserProfile(null);
        return { error: null };
      }
      
      // Session exists, proceed with sign out
      const { error } = await supabase?.auth?.signOut();
      
      // Always clear local state regardless of sign out result
      setUser(null);
      setUserProfile(null);
      
      // Log error but don't throw - user is effectively signed out
      if (error) {
        console.warn('Sign out warning (user state cleared):', error?.message);
      }
      
      return { error: null };
    } catch (error) {
      // Catch any unexpected errors, clear state anyway
      console.error('Sign out error (clearing state anyway):', error);
      setUser(null);
      setUserProfile(null);
      return { error: null }; // Return success since local state is cleared
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email);
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Admin password reset for superusers
  const adminResetUserPassword = async (userId, newPassword) => {
    try {
      console.log('=== Starting admin password reset ===');
      console.log('User ID:', userId);
      console.log('Password length:', newPassword?.length);

      // Validate new password
      if (!newPassword || newPassword?.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Get current session token
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Failed to get current session: ' + sessionError?.message);
      }
      
      if (!session) {
        console.error('No active session found');
        throw new Error('No active session - please log in again');
      }

      console.log('Session obtained, calling Edge Function...');

      // Call the Edge Function to reset password with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller?.abort(), 30000); // 30 second timeout

      try {
        const response = await supabase?.functions?.invoke('admin-reset-password', {
          body: JSON.stringify({
            userId,
            newPassword
          }),
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json'
          },
          signal: controller?.signal
        });

        clearTimeout(timeoutId);
        console.log('Edge Function raw response:', response);

        // Handle function invocation errors
        if (response?.error) {
          console.error('Edge Function invocation error:', response?.error);
          
          // Extract detailed error message
          let errorMessage = 'Failed to reset password';
          
          if (response?.error?.message) {
            errorMessage = response?.error?.message;
          } else if (response?.error?.context?.body) {
            try {
              const errorBody = JSON.parse(response?.error?.context?.body);
              errorMessage = errorBody?.error || errorBody?.details || errorMessage;
            } catch (parseError) {
              console.error('Failed to parse error body:', parseError);
            }
          }
          
          throw new Error(errorMessage);
        }

        // Parse and validate the response data
        let responseData = response?.data;
        
        // If data is a string, try to parse it as JSON
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            console.error('Failed to parse response data:', parseError);
            throw new Error('Invalid response format from server');
          }
        }

        console.log('Parsed response data:', responseData);

        // Check if the Edge Function returned an error in the response body
        if (responseData?.error) {
          let errorMessage = responseData?.details 
            ? `${responseData?.error}: ${responseData?.details}`
            : responseData?.error;
          console.error('Edge Function error response:', errorMessage);
          throw new Error(errorMessage);
        }

        // Check if the response indicates success
        if (!responseData?.success) {
          console.error('No success indicator in response:', responseData);
          throw new Error('Password reset failed - unexpected response from server');
        }

        console.log('Password reset successful');
        return { data: responseData, error: null };

      } catch (invokeError) {
        clearTimeout(timeoutId);
        
        // Handle timeout specifically
        if (invokeError?.name === 'AbortError') {
          console.error('Edge Function timeout');
          throw new Error('Request timeout - please try again');
        }
        
        throw invokeError;
      }

    } catch (error) {
      console.error('=== adminResetUserPassword error ===');
      console.error('Error type:', error?.constructor?.name);
      console.error('Error message:', error?.message);
      console.error('Error stack:', error?.stack);
      
      return { 
        data: null, 
        error: {
          message: error?.message || 'Failed to reset password - unknown error'
        }
      };
    }
  };

  // User management methods (for superuser)
  const createUser = async (email, password, username, fullName, role) => {
    try {
      // First, check if user already exists to prevent duplicates
      const { data: existingUser } = await supabase?.from('user_profiles')?.select('email, username')?.or(`email.eq.${email},username.eq.${username}`)?.single();

      if (existingUser) {
        throw new Error(`User with email ${email} or username ${username} already exists`);
      }

      // Create auth user with proper metadata - let trigger handle profile creation
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            username,
            full_name: fullName,
            role
          }
        }
      });

      if (authError) {
        console.error('Auth user creation error:', authError);
        throw authError;
      }

      if (!authData?.user?.id) {
        throw new Error('User creation failed - no user ID returned');
      }

      // Wait for the trigger to create the profile
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Check if the trigger created the user profile
      let profileCheck;
      let retryCount = 0;
      const maxRetries = 5; // Increased retries

      while (retryCount < maxRetries) {
        const { data: checkData, error: checkError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', authData?.user?.id)?.single();
        
        if (!checkError && checkData) {
          profileCheck = checkData;
          break;
        }
        
        if (checkError && checkError?.code !== 'PGRST116') {
          console.error('Error checking user profile:', checkError);
        }
        
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1500)); // Longer wait between retries
        }
      }

      // If profile still doesn't exist after retries, use multiple creation strategies
      if (!profileCheck) {
        console.log('Creating user profile using fallback creation strategies');
        
        // Strategy 1: Try the resilient safe creation function
        try {
          const { data: profileData, error: profileError } = await supabase?.rpc('create_user_profile_safe', {
            profile_id: authData?.user?.id,
            profile_email: email,
            profile_username: username,
            profile_full_name: fullName || username,
            profile_role: role || 'user',
            profile_is_active: true,
            creator_id: user?.id  // Pass current user as creator
          });

          if (!profileError && profileData) {
            console.log('User profile created successfully via safe creation function');
            profileCheck = profileData;
          }
        } catch (rpcError) {
          console.warn('Safe RPC call failed, trying simple function:', rpcError);
        }

        // Strategy 2: Try simple function if safe function failed
        if (!profileCheck) {
          try {
            const { data: simpleData, error: simpleError } = await supabase?.rpc('create_user_profile_simple', {
              profile_id: authData?.user?.id,
              profile_email: email,
              profile_username: username,
              profile_full_name: fullName || username
            });

            if (!simpleError && simpleData) {
              console.log('User profile created successfully via simple creation function');
              profileCheck = simpleData;
            }
          } catch (simpleRpcError) {
            console.warn('Simple RPC call failed, trying direct insertion:', simpleRpcError);
          }
        }

        // Strategy 3: Final fallback - direct insertion
        if (!profileCheck) {
          try {
            const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.insert({
                id: authData?.user?.id,
                email: email,
                username: username,
                full_name: fullName || username,
                role: role || 'user',
                is_active: true,
                email_verified: false
                // Deliberately omit created_by and updated_by to avoid FK constraint violation
              })?.select()?.single();

            if (!profileError) {
              console.log('User profile created successfully via direct insertion fallback');
              profileCheck = profileData;
            } else {
              console.error('Final fallback profile creation failed:', profileError);
            }
          } catch (insertError) {
            console.error('Direct insertion also failed:', insertError);
          }
        }

        if (!profileCheck) {
          throw new Error('Failed to create user profile after all attempts');
        }
      }

      // Update additional fields if needed (role, created_by/updated_by)
      if (profileCheck && (profileCheck?.role !== role || !profileCheck?.created_by)) {
        const updateFields = {};
        
        if (profileCheck?.role !== role && role !== 'user') {
          updateFields.role = role;
        }
        
        // Only set created_by/updated_by if current user exists and is different from new user
        if (user?.id && user?.id !== authData?.user?.id) {
          updateFields.updated_by = user?.id;
          updateFields.updated_at = new Date()?.toISOString();
          
          // Only set created_by if it's currently null
          if (!profileCheck?.created_by) {
            updateFields.created_by = user?.id;
          }
        }

        if (Object.keys(updateFields)?.length > 0) {
          const { error: updateError } = await supabase?.from('user_profiles')?.update(updateFields)?.eq('id', authData?.user?.id);

          if (updateError) {
            console.warn('Failed to update user profile fields:', updateError);
            // Don't throw error since profile creation was successful
          }
        }
      }

      return { data: authData, error: null };
    } catch (error) {
      console.error('Create user error:', error);
      return { data: null, error };
    }
  };

  const updateUserProfile = async (userId, updates) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString(),
          updated_by: user?.id
        })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      
      // If updating current user, refresh profile
      if (userId === user?.id) {
        setUserProfile(data);
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const deleteUser = async (userId) => {
    try {
      // First delete user profile
      const { error: profileError } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

      if (profileError) throw profileError;

      // Then delete auth user (requires admin privileges)
      // Note: This would typically be done through a server-side admin function
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const getAllUsers = async () => {
    try {
      // Add enhanced error logging and fallback strategies
      console.log('Fetching all users...', { currentUser: user?.id, userRole: userProfile?.role });

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users:', error);
        
        // If RLS is blocking access, try alternative approach for debugging
        if (error?.code === 'PGRST301' || error?.code === '42501') {
          console.log('RLS may be blocking access. Current user role:', userProfile?.role);
          console.log('Attempting to fetch current user profile to verify superuser status...');
          
          // Verify current user's permissions
          const { data: currentUserCheck } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();
          console.log('Current user profile check:', currentUserCheck);
        }
        
        throw error;
      }
      
      console.log(`Successfully fetched ${data?.length || 0} users`);
      return { data, error: null };
    } catch (error) {
      console.error('getAllUsers error:', error);
      return { data: null, error };
    }
  };

  // Role checking helper methods
  const isSuperUser = () => {
    return userProfile?.role === 'superuser';
  };

  const isEditor = () => {
    return userProfile?.role === 'editor' || userProfile?.role === 'superuser';
  };

  const isUser = () => {
    return userProfile?.role === 'user';
  };

  const hasPermission = (requiredRole) => {
    if (!userProfile?.role) return false;
    
    const roleHierarchy = {
      user: 1,
      editor: 2,
      superuser: 3
    };
    
    return roleHierarchy?.[userProfile?.role] >= roleHierarchy?.[requiredRole];
  };

  const value = {
    user,
    userProfile,
    loading,
    initialized,
    signIn,
    signUp,
    signOut,
    resetPassword,
    adminResetUserPassword,
    refreshSession,
    createUser,
    updateUserProfile,
    deleteUser,
    getAllUsers,
    isSuperUser,
    isEditor,
    isUser,
    hasPermission,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
export { AuthContext };